/**
 * Where a rost lies, said as a piece of wall — and the five questions a
 * drawing surface has to ask before it may put one there: may this wall take
 * one, which side of it is outside, how wide is the element, where along the
 * wall can it lie, and what is it called.
 *
 * Pure geometry in millimetres — no React, no SVG, no screen coordinates.
 * Same rule as `draft.ts`, one step further along the chain: the board holds
 * placements and shows them, and every statement about where a rost may lie
 * is made here, where it can be tested without a browser.
 *
 * Three things it deliberately does **not** do:
 *
 * - **It carries no figure out of the domain rulebook.** Not the 400 and the
 *   1.500 a rost element measures, not the widths that follow from a type.
 *   Those travel with the answer (`GratingSpec`, design §3.2) or come in as an
 *   argument, because a threshold typed into the presentation layer is a
 *   domain statement in the one place nobody tests it against the backend.
 *   The single number that does live here is the ten-millimetre grid, and it
 *   is a pointing-device convenience rather than a rule (see {@link GRID_MM}).
 * - **It passes no judgement.** Whether a plan is wrong — too long, overlapping,
 *   hanging over the end, a remainder nobody may build — is decided by
 *   `planning` and comes back as a finding (design §3.5, §3.5.1). What happens
 *   here is placing: the editor snaps, clamps and refuses a drop, so those
 *   states are not produced by accident. Checked again in the backend all the
 *   same, for the reason `manual.py` gives: a guarantee only the client keeps
 *   is not one.
 * - **It never turns a drawn distance into a measurement.** A wall's length is
 *   what somebody asserted, and a wall that carries no assertion carries no
 *   rost (§3.4, A-014). `drawLengthMm` exists on every segment and is not read
 *   anywhere in this file.
 */
import { isSegmentRead, type DemoOutline, type DemoSegment } from "./outline";

/** What a placement is. `bracket` is an *additional* console, never one of the
 * counted ones; `area` is hatching in front of the facade and goes into no
 * parts list (design §5.2). */
export type PlacementKind = "grating" | "gap" | "bracket" | "area";

/**
 * The backend's own word for a grating type — `"pressrost"` and its siblings.
 *
 * A bare alias and not a union of the three names we know today, for the same
 * reason the widths are not in this file: the catalogue is on the wire
 * (`GratingSpec`, §3.2), and a fourth type would have to be added here before
 * an answer that already carries it could be drawn. `DemoGrating.type` says
 * `string | null` on the wire for the same reason.
 */
export type GratingType = string;

/**
 * One piece of wall, and what is on it.
 *
 * No coordinates, no side and no bar direction — every one of them absent on
 * purpose (design §3.1). A placement moves with the wall when the outline
 * changes, instead of hanging in the air two millimetres off the facade while
 * still looking right on screen.
 */
/**
 * One corner somebody treated, by the wall whose **end** it is.
 *
 * A corner and not two element ends: every corner is the end of exactly one
 * wall, the ring seam included, so one corner is one entry and cannot
 * disagree with itself (design 2026-08-27 §3).
 *
 * Two treatments and they exclude each other. `gehrung` divides the corner
 * square with a diagonal and both gratings are cut; `fuellung` lets the one
 * named in `filledWallIndex` cover it whole while the other butts against it.
 * Both overrides are `null` in the ordinary case and mean *what the answer
 * derives*.
 */
export interface CornerMark {
  wallIndex: number;
  treatment: "gehrung" | "fuellung";
  filledWallIndex: number | null;
  kindOverride: "aussen" | "innen" | null;
  gainOverrideMm: number | null;
}

export interface Placement {
  id: string;
  kind: PlacementKind;
  /** Which side of the house — index into the walls of the computed outline. */
  wallIndex: number;
  /** Start, from the wall's beginning in walking direction, in mm. */
  offsetMm: number;
  /** Extent along the wall, in mm. */
  lengthMm: number;
  /** Only on `grating`. `null` means the type of the whole job, never "none". */
  gratingType: GratingType | null;

  /**
   * A blank somebody typed instead of the one the mitre arithmetic derives.
   *
   * `null` is the normal case and means *whatever it comes to* — the same
   * reading `gratingType` above and `widthMm` below have. Its own figure and
   * not a corrected `lengthMm`, because the mitre lies across the corner
   * square and not along the wall: an element can be longer than the wall
   * stretch it occupies (E-47).
   */
  blankLengthMm: number | null;

  /**
   * What the derivation said at the moment that blank was typed.
   *
   * Carried, never calculated with. It is how the screen can say that a typed
   * production length has drifted away from the shape it was typed against —
   * the wall got shorter, the mitre came off — instead of standing there
   * quietly being wrong.
   */
  blankSnapshotMm: number | null;
  /**
   * Only on `grating`, and only where somebody departs from the catalogue
   * width — `null` is the normal case and means "the width of the type"
   * (design §3.2.1).
   *
   * A special width is allowed and shown, not blocked: A-2 is one of the two
   * cases in the stock with a reference solution, and its element list carries
   * a width that is in no catalogue. A screen that cannot reproduce the one
   * answer we know cannot do the job it was built for. The open question about
   * whether that width is permissible belongs to the domain owner (MK-27) and
   * is not answered by a constant in the browser.
   */
  widthMm: number | null;
  /**
   * Where a special width lies across the wall. Absent or `false` is the old
   * reading: the outer edge stays on the type's line and the inner one moves,
   * as in a width zone. `true` keeps the inner edge on the wall and moves the
   * outer one — the recessed wall, whose grating is built 175 mm wider so it
   * runs flush with its neighbours (Denis, 31.10.2026).
   *
   * Optional because every plan stored before it lacks it. Only the drawing
   * reads it; the area and the bracket follow from `widthMm` alone.
   */
  widensOutward?: boolean;
  /**
   * Only on `gap`: does this interruption end the run, or does the run walk
   * over it (design §3.7)?
   *
   * A door, a step or an extension ends it. A downpipe, a shaft or a notch
   * does not — it is one element with a hole in it, not two elements with a
   * gap between them. Getting this wrong costs a console per occurrence, and
   * A-10 alone has four downpipes.
   *
   * Nothing in this module reads it: whether an interruption splits a run is a
   * statement about runs and consoles, and runs are formed in `planning`. What
   * matters here is that an interruption takes up wall either way.
   */
  splitsRun: boolean;
  /** „W3-02". Handed out on creation, overwritable. */
  label: string | null;
  /** Only on `area` — how far the hatching reaches from the wall. */
  depthMm: number | null;
}

/**
 * The four figures a grating type carries, as the answer delivers them.
 *
 * Declared here so the store and the board name one type; the values are never
 * built in this module. Design §3.2: with the catalogue on the wire, the board
 * may compute `free − length` and compare it against a number it was given —
 * without it, `400` would stand in the browser as a constant.
 */
export interface GratingSpec {
  type: GratingType;
  widthMm: number;
  minLengthMm: number;
  maxLengthMm: number;
  standardLengthMm: number;
}

/**
 * How wide this element really is — its own special width, else the width of
 * its type, else `null`.
 *
 * The three steps are the three rules of §3.2 and §3.2.1 in order: a width
 * somebody typed wins (special widths exist in the stock and are shown rather
 * than blocked); `gratingType: null` means the type of the job and never
 * "none"; and where no type is settled at all, **nothing is guessed** — a
 * width is a manufacturing figure, and A-014 forbids inventing one. `null`
 * is what the board draws no band for and what makes the screen ask.
 *
 * Nothing is judged here. Whether a special width is permissible is an open
 * domain question (MK-27) and comes back from the endpoint as a marking, not
 * as a refusal.
 */
export function effectiveWidthMm(
  placement: Placement,
  specs: readonly GratingSpec[],
  jobGratingType: GratingType | null,
): number | null {
  if (placement.kind !== "grating") return null;
  if (placement.widthMm !== null) return placement.widthMm;

  const type = placement.gratingType ?? jobGratingType;
  if (type === null) return null;
  return specs.find((spec) => spec.type === type)?.widthMm ?? null;
}

/** A stretch of wall nothing lies on, in the wall's own millimetres. */
export interface FreeStretch {
  offsetMm: number;
  lengthMm: number;
}

/** What an offset came to rest against — `"grid"` gets no mark on screen
 * (design §5.4: a mark that is always there marks nothing). */
export type SnapTarget = "neighbour" | "wallStart" | "wallEnd" | "grid";

export interface Snap {
  offsetMm: number;
  snappedTo: SnapTarget;
}

/**
 * The step every offset lands on when nothing better is near, in mm.
 *
 * Not a domain figure. It is the resolution this screen works in — the same
 * step the arrow keys move by in `OutlineReview`, so a dragged element and a
 * typed one end up on the same numbers. Exported so the board does not grow a
 * second copy of it next to the keyboard handler.
 */
export const GRID_MM = 10;

/**
 * The kinds that take up the wall.
 *
 * A `bracket` sits under a run and an `area` in front of the facade; treating
 * either as occupied would let the three side tools push the rost around, and
 * a console that cannot be placed where the run is, is not a console. Only
 * `grating` and `gap` are stretches of wall that are spoken for.
 */
const OCCUPYING_KINDS: ReadonlySet<PlacementKind> = new Set<PlacementKind>(["grating", "gap"]);

function wallOf(outline: DemoOutline, wallIndex: number): DemoSegment | null {
  return outline.segments[wallIndex] ?? null;
}

/**
 * How long this wall is *claimed* to be — never how long it was drawn.
 *
 * `null` where nobody claimed anything, and `null` for a wall that does not
 * exist. Defers to `isSegmentRead` rather than re-deriving the `read`/`manual`
 * test, which that function's own docstring asks for: a rule checked in one
 * place cannot drift from itself at a second.
 */
export function wallLengthMm(outline: DemoOutline, wallIndex: number): number | null {
  const wall = wallOf(outline, wallIndex);
  if (!wall || !isSegmentRead(wall)) return null;
  return wall.lengthMm;
}

/**
 * Whether a rost may be put on this wall at all (design §3.4).
 *
 * Only a wall with an asserted measure — read off the drawing or typed by a
 * person. One that was merely drawn (`sketch`) or forced by the ring
 * (`closure`) carries no figure, and an element on it would take its length
 * from a drawn distance, which is exactly what A-014 forbids. G-2 holds the
 * question whether a forced length should count; the existing test says no,
 * and so does this.
 *
 * Not `state`: what the reading said is on a wall is not a permission. This
 * screen exists so a person can say something else.
 */
export function wallAcceptsGrating(outline: DemoOutline, wallIndex: number): boolean {
  return wallLengthMm(outline, wallIndex) !== null;
}

/**
 * Which side of a wall is outside, as a multiplier on the wall's left-hand
 * normal `(-dy, dx)` — the same normal `projection.ts` builds for a dimension
 * line and a rail pair, so a drawing layer needs one formula and not two.
 *
 * Read off the winding of the walk (shoelace over the corner list), because
 * "away from the middle" is wrong in a Rücksprung — and a Rücksprung is the
 * normal case, it is the reason the corner pull exists in the drawing editor.
 * The winding answers for the whole shape at once; that is the point of using
 * it, and it is why `wallIndex` only matters in the fallback below.
 *
 * **An open walk is closed for this one sum** (design §3.3): the wrap-around
 * term joins the last corner back to the first, which is exactly the virtual
 * closure, and costs nothing on a closed walk whose last corner is a second
 * copy of its first (a zero-length step contributes nothing). This is the only
 * place in the system where an open walk pretends to be closed.
 *
 * Where the area comes out at nothing — a degenerate walk this editor cannot
 * produce — it falls back to "away from the centre of the bounding box", the
 * same approximation `notchTarget` uses.
 */
export function outwardSign(outline: DemoOutline, wallIndex: number): 1 | -1 {
  const twiceArea = shoelaceTwiceArea(outline.nodes);
  // Integer millimetres make this sum an integer, so "under one" is "zero".
  if (Math.abs(twiceArea) >= 1) return twiceArea > 0 ? -1 : 1;
  return awayFromBoundingBoxCentre(outline, wallIndex);
}

function shoelaceTwiceArea(nodes: readonly { xMm: number; yMm: number }[]): number {
  let sum = 0;
  for (let index = 0; index < nodes.length; index += 1) {
    const a = nodes[index]!;
    const b = nodes[(index + 1) % nodes.length]!;
    sum += a.xMm * b.yMm - b.xMm * a.yMm;
  }
  return sum;
}

function awayFromBoundingBoxCentre(outline: DemoOutline, wallIndex: number): 1 | -1 {
  const from = outline.nodes[wallIndex];
  const to = outline.nodes[wallIndex + 1];
  // Nothing left to read: no wall, or a walk of one point. The walk this
  // editor produces winds positively, and that answer is -1.
  if (!from || !to) return -1;

  const xs = outline.nodes.map((node) => node.xMm);
  const ys = outline.nodes.map((node) => node.yMm);
  const centreX = (Math.min(...xs) + Math.max(...xs)) / 2;
  const centreY = (Math.min(...ys) + Math.max(...ys)) / 2;

  const leftX = -(to.yMm - from.yMm);
  const leftY = to.xMm - from.xMm;
  const midX = (from.xMm + to.xMm) / 2;
  const midY = (from.yMm + to.yMm) / 2;

  return (midX - centreX) * leftX + (midY - centreY) * leftY >= 0 ? 1 : -1;
}

/**
 * The millimetre box one placement occupies, its own width included.
 *
 * Its own function and in this file, because it needs the outline, the
 * grating specs and the winding — all of which live here — and none of the
 * screen's pixels. Feeds „Diesen Rost groß" (`PlanningScreen.zoomToPlacement`,
 * design 2026-09-10): a view is a millimetre box handed to `viewForBox`, and
 * nothing about where that box comes from belongs in the screen layer.
 *
 * `null` wherever there is nothing to bound: no such wall, or a wall nobody
 * asserted a length for (§3.4) — the same guard `wallLengthMm` already keeps,
 * read through it rather than re-checked here.
 */
export function placementBoxMm(
  outline: DemoOutline,
  placement: Placement,
  specs: readonly GratingSpec[],
  jobGratingType: GratingType | null,
): { minXMm: number; minYMm: number; maxXMm: number; maxYMm: number } | null {
  const from = outline.nodes[placement.wallIndex];
  const to = outline.nodes[placement.wallIndex + 1];
  if (!from || !to) return null;

  const lengthMm = wallLengthMm(outline, placement.wallIndex);
  if (lengthMm === null || lengthMm <= 0) return null;

  const ux = (to.xMm - from.xMm) / lengthMm;
  const uy = (to.yMm - from.yMm) / lengthMm;
  const sign = outwardSign(outline, placement.wallIndex);
  const nx = -uy * sign;
  const ny = ux * sign;

  const widthMm = effectiveWidthMm(placement, specs, jobGratingType) ?? 0;
  const startX = from.xMm + ux * placement.offsetMm;
  const startY = from.yMm + uy * placement.offsetMm;
  const endX = startX + ux * placement.lengthMm;
  const endY = startY + uy * placement.lengthMm;

  const xs = [startX, endX, startX + nx * widthMm, endX + nx * widthMm];
  const ys = [startY, endY, startY + ny * widthMm, endY + ny * widthMm];
  return {
    minXMm: Math.min(...xs),
    minYMm: Math.min(...ys),
    maxXMm: Math.max(...xs),
    maxYMm: Math.max(...ys),
  };
}

/**
 * The stretches of one wall nothing lies on, in walking order.
 *
 * Empty on a wall that takes no placement at all (§3.4) — which is not the
 * same statement as "this wall is full", and a caller that wants to tell the
 * two apart asks {@link wallAcceptsGrating}.
 *
 * Everything is clipped to the wall and merged, because the endpoint accepts a
 * document this editor would not have produced: an overlap and an overhang
 * come back as findings with the plan still drawn (§3.5.1), and a stretch list
 * that counted the same millimetre twice would then offer space that is not
 * there.
 */
export function freeStretches(
  outline: DemoOutline,
  placements: readonly Placement[],
  wallIndex: number,
): FreeStretch[] {
  const wallLength = wallLengthMm(outline, wallIndex);
  if (wallLength === null || wallLength <= 0) return [];

  const taken = placements
    .filter((placement) => placement.wallIndex === wallIndex && OCCUPYING_KINDS.has(placement.kind))
    .map((placement) => ({
      from: Math.min(Math.max(placement.offsetMm, 0), wallLength),
      to: Math.min(Math.max(placement.offsetMm + placement.lengthMm, 0), wallLength),
    }))
    .filter((span) => span.to > span.from)
    .sort((a, b) => a.from - b.from);

  const stretches: FreeStretch[] = [];
  let cursor = 0;
  for (const span of taken) {
    if (span.from > cursor) stretches.push({ offsetMm: cursor, lengthMm: span.from - cursor });
    cursor = Math.max(cursor, span.to);
  }
  if (cursor < wallLength) {
    stretches.push({ offsetMm: cursor, lengthMm: wallLength - cursor });
  }
  return stretches;
}

/**
 * Where an element of `lengthMm` comes to rest when the pointer asks for
 * `wantedMm` — or `null` when it cannot rest anywhere near there.
 *
 * Two jobs, and they are not the same one: pull the wanted offset onto a rest
 * point, **and** check the element is free to lie there. A rest point the
 * element cannot lie on does not win for having been the nearest — the next
 * candidate takes its turn, and when none is left the answer is `null` and the
 * board draws no ghost (design §5.4: nothing is worse than a preview that
 * promises something).
 *
 * The candidates, in the order design §5.3 lays down:
 *
 * 1. **A neighbour's edge**, nearest first. It is first because the domain
 *    statement hangs on it: touching or not touching is the difference between
 *    one run and two, and so between one console and two (§4.1). Nobody should
 *    have to hit that to the millimetre.
 * 2. **The wall's own two ends** — a snap when the pointer is near one, and a
 *    clamp when it has gone past. Those two ends are the limits of where the
 *    element may sit at all, so an offset beyond one of them is not a near
 *    miss but an overshoot.
 * 3. **The grid**, clamped back onto the wall. Not a snap radius: it is the
 *    resolution the whole screen works in, so it applies whether or not
 *    anything was in reach.
 *
 * `toleranceMm` comes in from the caller, converted from a radius in screen
 * pixels at the current scale (§5.3): snapping is a pointing-device aid, not a
 * geometry rule, and a radius in millimetres is unfeelable at the scales a
 * house produces on a 520-px board.
 *
 * **The caller caps it in millimetres, and that cap is not a detail:**
 * `min(12 px in mm, minGapMm / 2)`, with `minGapMm` out of the answer and not
 * out of the browser. Twelve pixels are 240 mm of real wall on a 9,4-m facade
 * and 440 mm on a 17,4-m one, while a downpipe measures 120 — an uncapped
 * radius eats a real interruption, and two runs quietly become one, which is a
 * console too few and shows up on the building site.
 *
 * This function is the second line of defence and not the first: a snap point
 * an element cannot lie on never wins, so an interruption cannot be swallowed
 * even when the caller hands in a reckless radius. The cap is still the
 * caller's job — the aim is that the pointer never *offers* to jump the gap.
 *
 * **A moving element is dropped from `placements` by the caller.** It cannot
 * be its own neighbour, and it must not block its own move.
 */
export function snapOffset(
  outline: DemoOutline,
  placements: readonly Placement[],
  wallIndex: number,
  wantedMm: number,
  lengthMm: number,
  toleranceMm: number,
): Snap | null {
  const wallLength = wallLengthMm(outline, wallIndex);
  if (wallLength === null || lengthMm <= 0 || lengthMm > wallLength) return null;

  const stretches = freeStretches(outline, placements, wallIndex);
  const maxOffsetMm = wallLength - lengthMm;
  const tolerance = Math.max(toleranceMm, 0);

  const near = (offsetMm: number) => Math.abs(offsetMm - wantedMm) <= tolerance;
  const distance = (offsetMm: number) => Math.abs(offsetMm - wantedMm);
  const fits = (offsetMm: number) =>
    offsetMm >= 0 &&
    offsetMm <= maxOffsetMm &&
    stretches.some(
      (stretch) =>
        stretch.offsetMm <= offsetMm &&
        offsetMm + lengthMm <= stretch.offsetMm + stretch.lengthMm,
    );

  // 1 — every edge an element could butt against, from either side.
  const edges = new Set<number>();
  for (const placement of placements) {
    if (placement.wallIndex !== wallIndex || !OCCUPYING_KINDS.has(placement.kind)) continue;
    edges.add(placement.offsetMm + placement.lengthMm);
    edges.add(placement.offsetMm - lengthMm);
  }
  const neighbours = [...edges]
    .filter(near)
    .sort((a, b) => distance(a) - distance(b));
  for (const offsetMm of neighbours) {
    if (fits(offsetMm)) return { offsetMm, snappedTo: "neighbour" };
  }

  // 2 — the wall's own ends, snapping and clamping in one.
  const ends: Snap[] = [];
  if (near(0) || wantedMm < 0) ends.push({ offsetMm: 0, snappedTo: "wallStart" });
  if (near(maxOffsetMm) || wantedMm > maxOffsetMm) {
    ends.push({ offsetMm: maxOffsetMm, snappedTo: "wallEnd" });
  }
  ends.sort((a, b) => distance(a.offsetMm) - distance(b.offsetMm));
  for (const end of ends) {
    if (fits(end.offsetMm)) return end;
  }

  // 3 — the grid. The clamp is belt and braces after step 2, and it is what
  // keeps a rounded offset from stepping over the wall end by five millimetres.
  const onGrid = Math.min(Math.max(Math.round(wantedMm / GRID_MM) * GRID_MM, 0), maxOffsetMm);
  return fits(onGrid) ? { offsetMm: onGrid, snappedTo: "grid" } : null;
}

const DIGITS_ONLY = /^\d+$/;

/**
 * The next label to hand out on one wall: `W3-01`, `W3-02`, …
 *
 * **The axis is the side of the house, not the job** (design §3.6). A-5, the
 * one real workshop drawing in the stock, numbers the way the parts are made —
 * north #1–7, west #1…#5 — and a number running through three walls is not
 * findable on a building site.
 *
 * **Counted off the highest number in use on that wall, never off how many
 * there are.** Delete `W3-02` and what is left is `W3-01` and `W3-03`, and the
 * next one is `W3-04` — a number that moves means something different on a
 * printed sheet than on the screen it was printed from.
 *
 * Counted off the **labels**, not off where the placements currently sit: a
 * label carries its wall in it, so one that travelled to another wall still
 * holds its number down here and cannot be handed out a second time. A label
 * somebody rewrote in words is no longer a number and stops counting. Whether
 * two placements ended up with the same label is a backend finding and not
 * decided here (G-11).
 *
 * **The wall number in the label counts from one; `wallIndex` counts from
 * zero, and this line is the only place the two meet** (design §3.6). The
 * drawing editor already says `Wand ${index + 1}` out loud, so a label reading
 * `W0-01` beside a heading reading „Wand 1" would be an off-by-one that nobody
 * notices until an order goes out against the wrong side of a house. On the
 * wire and in the backend the field stays `wall_index` and stays zero-based:
 * it is an index into a list, not a name.
 */
export function nextLabel(placements: readonly Placement[], wallIndex: number): string {
  const prefix = `W${wallIndex + 1}-`;
  let highest = 0;
  for (const placement of placements) {
    const label = placement.label;
    if (!label?.startsWith(prefix)) continue;
    const digits = label.slice(prefix.length);
    if (DIGITS_ONLY.test(digits)) highest = Math.max(highest, Number(digits));
  }
  return `${prefix}${String(highest + 1).padStart(2, "0")}`;
}

/**
 * Divide one grating into two along its wall — „Aufteilung“ on the element
 * itself (Denis, 20.10.2026), where „Rohling aufteilen“ only offers the one
 * division the splitting rule makes.
 *
 * The first piece is the element itself (its id, its label — nothing is
 * renumbered, §3.6), shortened to `firstMm`; the second takes a new id and
 * the next free number on the wall, and starts where the first ends. A typed
 * blank is dropped on both: it was typed for the whole element, and which
 * part of it belongs to which piece is not this function's to guess (A-014).
 * The mitre stays at its corner on its own — the answer cuts the pieces.
 *
 * `null` where it does not divide: no such grating, or a piece of no length.
 */
export function splitPlacement(
  placements: readonly Placement[],
  id: string,
  firstMm: number,
  newId: string,
): Placement[] | null {
  const target = placements.find((each) => each.id === id);
  if (!target || target.kind !== "grating") return null;
  const secondMm = target.lengthMm - firstMm;
  if (!Number.isInteger(firstMm) || firstMm <= 0 || secondMm <= 0) return null;
  const label = nextLabel(placements, target.wallIndex);
  return placements.flatMap((each) =>
    each.id !== id
      ? [each]
      : [
          { ...each, lengthMm: firstMm, blankLengthMm: null, blankSnapshotMm: null },
          {
            ...each,
            id: newId,
            offsetMm: each.offsetMm + firstMm,
            lengthMm: secondMm,
            blankLengthMm: null,
            blankSnapshotMm: null,
            label,
          },
        ],
  );
}

let splitCounter = 0;

/** An id for a piece a division made — unique per browser session. */
export function newSplitId(): string {
  splitCounter += 1;
  const unique =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().slice(0, 8)
      : `${Date.now()}`;
  return `pl-split-${splitCounter}-${unique}`;
}

/** The seven kinds of object that can stand on a wall (E-47 follow-up, PRD §8.5). */
export const FEATURE_TYPES = [
  "haustuer",
  "fenstertuer",
  "fensterbank",
  "fallrohr",
  "schacht",
  "stufe",
  "sonstiges",
] as const;

export type FeatureType = (typeof FEATURE_TYPES)[number];

/**
 * One object standing on a wall — a door, a sill, a downpipe.
 *
 * Not a `Placement`: a facade object does not occupy wall the way a rost or a
 * gap does (`freeStretches` and `snapOffset` do not read this type), and it
 * carries fields — `covered`, `widthOffsetMm` — that describe an effect on
 * the grating around it rather than a stretch of wall. `wallIndex` and
 * `offsetMm`/`lengthMm` are the zone it stands in, in the same wall-relative
 * millimetres every placement uses, so `featureAt` can be asked the same
 * question `freeStretches` answers for placements.
 */
export interface FacadeFeature {
  id: string;
  wallIndex: number;
  offsetMm: number;
  lengthMm: number;
  featureType: FeatureType;
  /** Whether the grating runs over this object or stops at it. */
  covered: boolean;
  /**
   * Signed, and typed on the object, never on the type. Negative is the
   * common case (a sill set back from the facade), positive is just as valid.
   */
  widthOffsetMm: number;
  /** Whether standing on this object ends a run, the same statement
   * `Placement.splitsRun` makes for a gap. */
  splitsRun: boolean;
  label: string | null;
  /**
   * Which side a door is hinged on (24.09.2026) — read through
   * {@link hingeOf}, never directly: absent means `"left"`, the server's own
   * default, and every object stored before the field existed is absent.
   * Drawn only on {@link SWING_TYPES}; carried on every type, so a type
   * change and back does not lose the side somebody chose.
   */
  hinge?: DoorHinge;
}

/**
 * The side a door's leaf hangs on, DIN 107: seen from the side it opens
 * towards — for a house door, from inside. `FacadeFeature.hinge` on the
 * server, word for word.
 */
export type DoorHinge = "left" | "right";

/** The objects drawn with a leaf and its swing — the two kinds of door. */
export const SWING_TYPES: ReadonlySet<FeatureType> = new Set<FeatureType>(["haustuer", "fenstertuer"]);

/** A door's hinge side, with the default the server reads for an absent one. */
export function hingeOf(feature: Pick<FacadeFeature, "hinge">): DoorHinge {
  return feature.hinge ?? "left";
}

/** The side a door hangs on in words, as the panel and the menu print it. */
export const HINGE_WORDS: Record<DoorHinge, string> = { left: "links", right: "rechts" };

/**
 * What a type fills in when an object is created — and nothing else.
 *
 * `covered`, `widthOffsetMm` and `splitsRun` live on the object after
 * creation; nothing downstream reads the type again to find them. That
 * matters here specifically for `widthOffsetMm`: **it starts at `0` for
 * every type.** A sill set back 55 mm is one customer's case (A-2), not a
 * confirmed trade rule — a default here would answer an open question in a
 * place nobody would think to look for it. Chiara has not confirmed it.
 *
 * **`fallrohr` is the one exception, and it is documented rather than
 * derived** (design §14, 30.08.2026): a downpipe lies *on* the grating and
 * asks for a hole. `withCutout` is that wish and nothing more — the hole
 * itself is a {@link Cutout} of its own since 12.09.2026 (D-1). `schacht`
 * stays an opening — a shaft is a hole nobody covers.
 */
export const FEATURE_DEFAULTS: Record<
  FeatureType,
  {
    covered: boolean;
    widthOffsetMm: number;
    splitsRun: boolean;
    /** Whether a new object of this type brings a linked hole with it. */
    withCutout: boolean;
    name: string;
    defaultLengthMm: number;
  }
> = {
  // Covered since 22.09.2026 (Denis: „auch um die Haustür herum werden
  // Gitterroste gesetzt") — the grating runs past the door like past a sill,
  // and the width departure is typed on the object, as it is there.
  // `splitsRun` stays `true` so that unticking „Rost liegt darauf" restores
  // the older reading — a door that ends the run — exactly.
  haustuer: { covered: true, widthOffsetMm: 0, splitsRun: true, withCutout: false, name: "Haustür", defaultLengthMm: 1010 },
  fenstertuer: { covered: true, widthOffsetMm: 0, splitsRun: true, withCutout: false, name: "Fenstertür", defaultLengthMm: 2000 },
  fensterbank: { covered: true, widthOffsetMm: 0, splitsRun: true, withCutout: false, name: "Fensterbank", defaultLengthMm: 1400 },
  // Covered, and it asks for a hole — which is a `Cutout` of its own since
  // 12.09.2026 (D-1), prefilled square at pipe width plus clearance (D-9,
  // D-10) by `linkedCutoutFor`. The old arithmetic — an opening that forces
  // a joint and therefore a bracket — stays one click away, by unticking
  // „Rost liegt darauf"; `splitsRun` is kept at `false` so that click
  // restores it exactly.
  fallrohr: { covered: true, widthOffsetMm: 0, splitsRun: false, withCutout: true, name: "Fallrohr", defaultLengthMm: 120 },
  schacht: { covered: false, widthOffsetMm: 0, splitsRun: false, withCutout: false, name: "Schacht", defaultLengthMm: 600 },
  stufe: { covered: false, widthOffsetMm: 0, splitsRun: true, withCutout: false, name: "Stufe", defaultLengthMm: 1000 },
  sonstiges: { covered: false, widthOffsetMm: 0, splitsRun: true, withCutout: false, name: "Objekt", defaultLengthMm: 500 },
};

/**
 * The next id to hand a new facade object: `F1`, `F2`, …
 *
 * Counted off the highest number in use, never off how many there are — the
 * same reasoning {@link nextLabel} carries one page up, and for the same
 * reason: delete `F2` and the next one handed out must not repeat it.
 */
export function nextFeatureId(features: readonly FacadeFeature[]): string {
  let highest = 0;
  for (const feature of features) {
    const match = /^F(\d+)$/.exec(feature.id);
    if (match) highest = Math.max(highest, Number(match[1]));
  }
  return `F${highest + 1}`;
}

// ── Cutouts — the hole as a thing of its own (design 2026-09-10 §4) ───────

export type CutoutShape = "rund" | "eckig";
export type ReferenceKind = "wall_start" | "wall_end" | "feature";
export type ReferenceSide = "before" | "after";

/**
 * How the customer names the hole's position along the wall (D-11).
 *
 * Display and input help, carried in the document so the parts list can
 * write „1.200 mm ab F2 (Fenstertür, dahinter)". `Cutout.offsetMm` stays the
 * stored truth, from the wall's start; a reference to an object that is gone
 * falls back to the wall start on screen and moves no number.
 */
export interface CutoutReference {
  kind: ReferenceKind;
  featureId: string | null;
  side: ReferenceSide | null;
}

export const WALL_START: CutoutReference = { kind: "wall_start", featureId: null, side: null };

/**
 * A hole in the grating — free, or linked to the object it came from.
 *
 * Anchored to a wall (D-2), never to a placement: „Wand neu aufteilen" throws
 * placements away, and a hole on one would die with it. Which element is
 * drilled is derived ({@link cutoutsOn}). **Centre on both axes** (D-3):
 * `offsetMm` from the wall's start, `acrossMm` from the band's near edge, the
 * facade-side sheet edge where the workshop measures (D-8).
 *
 * A linked hole (`featureId !== null`) has no position of its own along the
 * wall — {@link cutoutAlongMm} puts it at its object's middle, so „move the
 * pipe, the hole follows" is a consequence and not a rule anybody runs.
 */
export interface Cutout {
  id: string;
  wallIndex: number;
  offsetMm: number;
  acrossMm: number;
  shape: CutoutShape;
  diameterMm: number | null;
  sizeAlongMm: number | null;
  sizeAcrossMm: number | null;
  /** `featureType`'s vocabulary, verbatim — no second list (§4, §12). */
  obstacleType: FeatureType;
  /** „Maße bestätigt" (PRD §19.5). Locks nothing. */
  confirmed: boolean;
  /** The element label the release was given for — carried, never recomputed. */
  confirmedOn: string | null;
  featureId: string | null;
  reference: CutoutReference;
}

/** A new hole is 120 × 120 (`RR 120`, A-10; square first — Chiara, 12.09.2026, D-9). */
export const CUTOUT_DEFAULT_MM = 120;
/** Montagespiel on a linked downpipe hole: 10 mm until Chiara says otherwise (D-10, AN-26). */
export const CUTOUT_CLEARANCE_DEFAULT_MM = 10;

/** `A1`, `A2`, … — off the highest number in use, as {@link nextFeatureId}.
 * A migrated `A-F3` is not a number in this series and counts as none. */
export function nextCutoutId(cutouts: readonly Cutout[]): string {
  let highest = 0;
  for (const cutout of cutouts) {
    const match = /^A(\d+)$/.exec(cutout.id);
    if (match) highest = Math.max(highest, Number(match[1]));
  }
  return `A${highest + 1}`;
}

/**
 * Where the hole's middle is along the wall — resolved.
 *
 * A linked hole is at its object's middle, floor on an odd length (the
 * workshop reads whole millimetres; `schema.resolved_cutouts` floors the
 * same way). A free hole is where it says. A linked hole whose object is
 * gone answers its stored figure — the server cascade removes such a hole,
 * so this is a fallback for a draft mid-gesture, not a state that persists.
 */
export function cutoutAlongMm(cutout: Cutout, features: readonly FacadeFeature[]): number {
  if (cutout.featureId === null) return cutout.offsetMm;
  const anchor = features.find((each) => each.id === cutout.featureId);
  if (!anchor) return cutout.offsetMm;
  return anchor.offsetMm + Math.floor(anchor.lengthMm / 2);
}

/** Full extent on both axes — the diameter twice where round. */
export function cutoutExtentMm(cutout: Cutout): { alongMm: number; acrossMm: number } {
  if (cutout.shape === "rund") {
    const d = cutout.diameterMm ?? 0;
    return { alongMm: d, acrossMm: d };
  }
  return { alongMm: cutout.sizeAlongMm ?? 0, acrossMm: cutout.sizeAcrossMm ?? 0 };
}

/**
 * Every hole drilled into this element, in wall order — **multi-valued**.
 *
 * The hole's own middle decides, half-open on the far end: a middle on the
 * joint belongs to the element that starts there. `cutout_carrier` in
 * `planning/cutouts.py` states the same rule for the finding; the two must
 * not disagree about which sheet the workshop drills (walk of 30.08.2026).
 */
export function cutoutsOn(
  cutouts: readonly Cutout[],
  features: readonly FacadeFeature[],
  placement: Placement,
): Cutout[] {
  if (placement.kind !== "grating") return [];
  return cutouts
    .map((cutout) => ({ cutout, alongMm: cutoutAlongMm(cutout, features) }))
    .filter(
      ({ cutout, alongMm }) =>
        cutout.wallIndex === placement.wallIndex &&
        placement.offsetMm <= alongMm &&
        alongMm < placement.offsetMm + placement.lengthMm,
    )
    .sort((a, b) => a.alongMm - b.alongMm)
    .map(({ cutout }) => cutout);
}

/**
 * The grating under `alongMm` on `wallIndex`, or `null` — half-open on the
 * far end, so a point on a joint belongs to the element that starts there:
 * the same rule {@link cutoutsOn} keeps for a hole's middle. One function
 * for the click that sets a hole (board and menu alike), so both read the
 * band under the click the same way.
 */
export function carrierAt(
  placements: readonly Placement[],
  wallIndex: number,
  alongMm: number,
): Placement | null {
  return (
    placements.find(
      (each) =>
        each.kind === "grating" &&
        each.wallIndex === wallIndex &&
        each.offsetMm <= alongMm &&
        alongMm < each.offsetMm + each.lengthMm,
    ) ?? null
  );
}

/**
 * The width of the band a hole sits in: the carrier's own effective width,
 * or — with no carrier under the point — the band the job's type would draw
 * there; `0` where nothing is settled. The one width rule the board's
 * `bandOf` and the screen's menu insert both read, so a hole set from the
 * band and one set from the menu land in the same middle.
 */
export function bandWidthMm(
  carrier: Placement | null,
  specs: readonly GratingSpec[],
  jobGratingType: GratingType | null,
): number {
  const own = carrier ? effectiveWidthMm(carrier, specs, jobGratingType) : null;
  return own ?? specs.find((spec) => spec.type === jobGratingType)?.widthMm ?? 0;
}

/**
 * The grid on both axes and **no clamp** (§8). A hole keeps its true place
 * and size; whether it fits is a finding's question, and a distance dragged
 * from 168 to 300 that moved nothing was the bug (§14.1 of the objects design).
 */
export function snapCutout(alongMm: number, acrossMm: number): { offsetMm: number; acrossMm: number } {
  const onGrid = (mm: number) => Math.round(mm / GRID_MM) * GRID_MM;
  return { offsetMm: onGrid(alongMm), acrossMm: onGrid(acrossMm) };
}

/**
 * The hole a covered object asks for (D-9, D-10): square, its middle at the
 * object's middle along and at half its own size across — touching the near
 * edge, the translation of the old „an der Fassade" into centre language
 * (§6.1).
 *
 * **Only a downpipe's width sizes its hole** — pipe width plus the case's
 * clearance on both sides. Every other type gets the default 120 × 120
 * (fix round 1 of task 12): a sill's length says how wide the sill is, not
 * how big a hole goes through the grating under it, and `lengthMm + clearance`
 * on a 1.400 mm Fensterbank produced a 1.410 mm square that nobody meant.
 *
 * The across figure lands **on the grid** (Schlussreview M-3), as
 * `freeCutoutAt` already does: 65 for a 130 mm hole would go to 70 on the
 * first purely along drag, and the figure would change without a hand on it.
 */
export function linkedCutoutFor(feature: FacadeFeature, clearanceMm: number, id: string): Cutout {
  const side = feature.featureType === "fallrohr" ? feature.lengthMm + clearanceMm : CUTOUT_DEFAULT_MM;
  return {
    id,
    wallIndex: feature.wallIndex,
    offsetMm: feature.offsetMm + Math.floor(feature.lengthMm / 2),
    acrossMm: snapCutout(0, side / 2).acrossMm,
    shape: "eckig",
    diameterMm: null,
    sizeAlongMm: side,
    sizeAcrossMm: side,
    obstacleType: feature.featureType,
    confirmed: false,
    confirmedOn: null,
    featureId: feature.id,
    reference: WALL_START,
  };
}

// ── Fallrohr-Schnellwahl (A5, 23.09.2026) ─────────────────────────────────

/**
 * The downpipe diameters a person picks with one click: 100 · 120 · 150 mm.
 *
 * **Pipe diameters, not hole sizes** — the hole is the pipe plus the case's
 * Montagespiel (D-10), exactly as {@link linkedCutoutFor} prefills it. 120 is
 * the catalogue default a new pipe already carries. Anything else is typed.
 * UI only: a choice sets the object's `lengthMm` and its hole's size fields;
 * nothing new travels on the wire.
 */
export const PIPE_PRESETS_MM = [100, 120, 150] as const;

/** Where a pipe of `pipeMm` starts so that its middle stays where it is —
 * the linked hole sits in that middle, and a new diameter must not move it.
 * Floored at the wall start; `settleFeature` puts it on the grid. */
export function pipeOffsetMm(feature: FacadeFeature, pipeMm: number): number {
  const middle = feature.offsetMm + Math.floor(feature.lengthMm / 2);
  return Math.max(middle - Math.floor(pipeMm / 2), 0);
}

/**
 * A linked hole resized for a pipe of `pipeMm`: pipe plus clearance, square
 * or round as it already is (D-9 keeps square the default, round stays
 * round).
 *
 * The across figure follows **only where it still is the prefill** — half
 * the old size, on the grid, the hole touching the facade-side edge
 * ({@link linkedCutoutFor}). A figure somebody typed or dragged is theirs.
 * Every other field is left exactly as it was, so the op this produces is
 * the one the size fields would have produced by hand.
 */
export function holeForPipe(cutout: Cutout, pipeMm: number, clearanceMm: number): Cutout {
  const side = pipeMm + clearanceMm;
  const oldSide = cutout.shape === "rund" ? (cutout.diameterMm ?? 0) : (cutout.sizeAcrossMm ?? 0);
  const wasPrefill = cutout.acrossMm === snapCutout(0, oldSide / 2).acrossMm;
  const acrossMm = wasPrefill ? snapCutout(0, side / 2).acrossMm : cutout.acrossMm;
  return cutout.shape === "rund"
    ? { ...cutout, diameterMm: side, acrossMm }
    : { ...cutout, sizeAlongMm: side, sizeAcrossMm: side, acrossMm };
}

/**
 * A free hole where somebody clicked: square 120, in the middle of the band
 * (§8) — the middle **on the grid**, since every drag rasters both axes
 * (`snapCutout`): 144 for a 288 band went to 140 on the first purely along
 * drag, and the across figure changed without anybody touching it (walk
 * B-2). Prefilled at 140, the drag moves it by whole steps or not at all.
 */
export function freeCutoutAt(wallIndex: number, alongMm: number, bandWidthMm: number, id: string): Cutout {
  return {
    id,
    wallIndex,
    offsetMm: alongMm,
    acrossMm: snapCutout(0, bandWidthMm / 2).acrossMm,
    shape: "eckig",
    diameterMm: null,
    sizeAlongMm: CUTOUT_DEFAULT_MM,
    sizeAcrossMm: CUTOUT_DEFAULT_MM,
    obstacleType: "sonstiges",
    confirmed: false,
    confirmedOn: null,
    featureId: null,
    reference: WALL_START,
  };
}

function anchorOf(reference: CutoutReference, features: readonly FacadeFeature[]): FacadeFeature | null {
  if (reference.kind !== "feature") return null;
  return features.find((each) => each.id === reference.featureId) ?? null;
}

/** The stored offset for a distance typed in the reference's own terms, or `null` where the reference names nothing. */
export function offsetFromReference(
  reference: CutoutReference,
  distanceMm: number,
  wallLengthMm: number,
  features: readonly FacadeFeature[],
): number | null {
  if (reference.kind === "wall_start") return distanceMm;
  if (reference.kind === "wall_end") return wallLengthMm - distanceMm;
  const anchor = anchorOf(reference, features);
  if (!anchor) return null;
  return reference.side === "before"
    ? anchor.offsetMm - distanceMm
    : anchor.offsetMm + anchor.lengthMm + distanceMm;
}

/** The distance in the reference's own terms for a stored offset — the inverse of {@link offsetFromReference}. */
export function distanceToReference(
  reference: CutoutReference,
  offsetMm: number,
  wallLengthMm: number,
  features: readonly FacadeFeature[],
): number | null {
  if (reference.kind === "wall_start") return offsetMm;
  if (reference.kind === "wall_end") return wallLengthMm - offsetMm;
  const anchor = anchorOf(reference, features);
  if (!anchor) return null;
  return reference.side === "before"
    ? anchor.offsetMm - offsetMm
    : offsetMm - (anchor.offsetMm + anchor.lengthMm);
}

/** The object a point on a wall lies in, or `null`. */
export function featureAt(
  features: readonly FacadeFeature[],
  wallIndex: number,
  offsetMm: number,
): FacadeFeature | null {
  return (
    features.find(
      (each) =>
        each.wallIndex === wallIndex &&
        each.offsetMm <= offsetMm &&
        offsetMm < each.offsetMm + each.lengthMm,
    ) ?? null
  );
}

/**
 * Whether a zone on one wall would run into an object already standing there.
 *
 * `exceptId` is the object being moved or resized — it may of course lie on
 * itself. Touching is not overlapping: an object ending at 2.000 and another
 * beginning there are two objects side by side, which is exactly how a door
 * and the sill beside it sit on a real facade.
 *
 * The one rule `_set_feature` on the server refuses outright
 * (`document.py`), stated here so the screen cannot build a document the
 * endpoint would reject — the drawing path and the drag path both went
 * straight past it until 30.08.2026 (walk finding 2).
 */
export function featureOverlaps(
  features: readonly FacadeFeature[],
  wallIndex: number,
  offsetMm: number,
  lengthMm: number,
  exceptId: string | null = null,
): boolean {
  return features.some(
    (each) =>
      each.id !== exceptId &&
      each.wallIndex === wallIndex &&
      Math.min(each.offsetMm + each.lengthMm, offsetMm + lengthMm) >
        Math.max(each.offsetMm, offsetMm),
  );
}

/**
 * What one object may come to rest at, having been asked for
 * `wantedOffsetMm`/`wantedLengthMm` — or why it may not.
 *
 * **The one place that decides where an object sits**, whichever gesture or
 * field asked: the drag of the whole thing, the drag of one end, an arrow key,
 * and the typed „Anfang"/„Länge" of the side panel. Round 2 of the acceptance
 * walk (30.08.2026) found the two typed fields going straight past it — an
 * offset typed over a neighbour rebuilt exactly the document `_set_feature`
 * refuses, and `_plan_features` does not check overlap either, so nothing
 * downstream caught it. Two places deciding one rule is how a rule stops being
 * one.
 *
 * The rule, in order: snap to {@link GRID_MM}, never shorter than one grid
 * step, never longer than its wall, never off either end of it, never on top
 * of a second object of the same wall. `"unchanged"` is its own answer and not
 * a quiet `"settled"`: a gesture that changes nothing must not push a step on
 * the undo stack, while a refusal it was carrying still has to be cleared.
 */
export type FeatureSettlement =
  | { outcome: "settled"; offsetMm: number; lengthMm: number }
  | { outcome: "unchanged" }
  | { outcome: "overlaps" };

export function settleFeature(
  features: readonly FacadeFeature[],
  feature: FacadeFeature,
  wantedOffsetMm: number,
  wantedLengthMm: number,
  wallLengthMm: number,
): FeatureSettlement {
  const onGrid = (mm: number) => Math.round(mm / GRID_MM) * GRID_MM;
  const lengthMm = Math.min(Math.max(onGrid(wantedLengthMm), GRID_MM), wallLengthMm);
  const maxOffsetMm = Math.max(wallLengthMm - lengthMm, 0);
  const offsetMm = Math.min(Math.max(onGrid(wantedOffsetMm), 0), maxOffsetMm);
  if (offsetMm === feature.offsetMm && lengthMm === feature.lengthMm) {
    return { outcome: "unchanged" };
  }
  if (featureOverlaps(features, feature.wallIndex, offsetMm, lengthMm, feature.id)) {
    return { outcome: "overlaps" };
  }
  return { outcome: "settled", offsetMm, lengthMm };
}

/**
 * Where a new object of `lengthMm` can stand on `wallIndex` nearest to
 * `wantedMm`, without overlapping an object already there — or `null` when
 * nothing of that length fits anywhere on the wall.
 *
 * `_set_feature` on the server refuses two features that overlap outright
 * (`document.py`), so a menu that places one exactly under the pointer would
 * hand the click straight back as a conflict banner more often than not — a
 * click is an approximate gesture, not a typed millimetre. This is the
 * client-side reason a right-click can never *produce* that refusal: it
 * shifts the object clear of its neighbours instead, the way `snapOffset`
 * already does for grating (§5.3) — and only where nothing at all is free
 * does it give up and say so (task 14, the „Objekt einsetzen" menu).
 *
 * Deliberately its own small pass over `features` rather than a second
 * caller of `freeStretches`: that function reads `Placement[]` and
 * `OCCUPYING_KINDS`, and a facade object is neither (see the `FacadeFeature`
 * docstring above) — bending it to fit would cost more than the few lines
 * repeated here.
 */
export function freeFeatureOffsetMm(
  features: readonly FacadeFeature[],
  wallIndex: number,
  wallLengthMm: number,
  wantedMm: number,
  lengthMm: number,
): number | null {
  if (lengthMm <= 0 || lengthMm > wallLengthMm) return null;

  const taken = features
    .filter((feature) => feature.wallIndex === wallIndex)
    .map((feature) => ({
      from: Math.min(Math.max(feature.offsetMm, 0), wallLengthMm),
      to: Math.min(Math.max(feature.offsetMm + feature.lengthMm, 0), wallLengthMm),
    }))
    .filter((span) => span.to > span.from)
    .sort((a, b) => a.from - b.from);

  const stretches: FreeStretch[] = [];
  let cursor = 0;
  for (const span of taken) {
    if (span.from > cursor) stretches.push({ offsetMm: cursor, lengthMm: span.from - cursor });
    cursor = Math.max(cursor, span.to);
  }
  if (cursor < wallLengthMm) stretches.push({ offsetMm: cursor, lengthMm: wallLengthMm - cursor });

  let best: number | null = null;
  let bestDistanceMm = Infinity;
  for (const stretch of stretches) {
    if (stretch.lengthMm < lengthMm) continue;
    const low = stretch.offsetMm;
    const high = stretch.offsetMm + stretch.lengthMm - lengthMm;
    const onGrid = Math.round(wantedMm / GRID_MM) * GRID_MM;
    const candidateMm = snapInsideStretch(Math.min(Math.max(onGrid, low), high), low, high);
    const distanceMm = Math.abs(candidateMm - wantedMm);
    if (distanceMm < bestDistanceMm) {
      best = candidateMm;
      bestDistanceMm = distanceMm;
    }
  }
  return best;
}

/**
 * Where a door set by keyboard goes: the middle of its wall, or the free
 * spot nearest to it (Entscheid Denis, 24.09.2026). At the wall's beginning
 * — where Enter used to put every object — the leaf swings onto the
 * neighbouring wall. `null` where no free stretch holds the door.
 */
export function centredFeatureOffsetMm(
  features: readonly FacadeFeature[],
  wallIndex: number,
  wallLengthMm: number,
  lengthMm: number,
): number | null {
  return freeFeatureOffsetMm(features, wallIndex, wallLengthMm, (wallLengthMm - lengthMm) / 2, lengthMm);
}

/**
 * Pulls a clamp result back onto the grid where the clamp is what moved it
 * off — a neighbour ending at 1.234 mm otherwise handed back an off-grid
 * 1.234 as the offset for the next object (review round 1, task 14, minor
 * 6). Only the two boundary cases move: `clamped` already equals `onGrid`
 * (nothing was clamped) is left alone by the caller, never reaching here.
 *
 * Snaps *inward*, away from the edge that produced the clamp — a step
 * outward would put the object back on top of the neighbour it was clamped
 * off of. Where no grid step fits inside `[low, high]` at all, the raw edge
 * is the only answer there is, and is returned unchanged.
 */
function snapInsideStretch(clamped: number, low: number, high: number): number {
  if (clamped === low) {
    const inward = Math.ceil(low / GRID_MM) * GRID_MM;
    return inward <= high ? inward : clamped;
  }
  if (clamped === high) {
    const inward = Math.floor(high / GRID_MM) * GRID_MM;
    return inward >= low ? inward : clamped;
  }
  return clamped;
}
